import { inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { BleClient, ScanMode } from '@capacitor-community/bluetooth-le';
import { BehaviorSubject, Observable, Subject, from, of, throwError } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import type { BleDeviceInfoExtended, BleServiceResponse, DeviceData } from '../../interfaces/ble.interface';
import { BleConnectionState, DeviceType } from '../../interfaces/ble.interface';

import type { BaseDeviceAdapter } from './adapters/base-device.adapter';
import { CannesDeviceAdapter } from './adapters/cannes-device.adapter';
import { Dti6DeviceAdapter } from './adapters/dti6-device.adapter';
import { identifyBleErrorType } from './ble-device-manager.helper';

@Injectable({
  providedIn: 'root',
})
export class BleDeviceManagerService {
  private readonly SCAN_TIMEOUT_MS = 120_000; // 2 minutes
  private readonly CLEANUP_DELAY_MS = 1000;

  private readonly adapters: Map<DeviceType, BaseDeviceAdapter>;

  // Connection state
  private readonly connectionStateSubject$ = new BehaviorSubject<BleConnectionState>(BleConnectionState.DISCONNECTED);
  private readonly connectedDeviceSubject$ = new BehaviorSubject<BleDeviceInfoExtended | undefined>(undefined);
  private readonly deviceDataSubject$ = new BehaviorSubject<DeviceData>({});

  private readonly bleEnabledSubject$ = new BehaviorSubject<boolean>(true);

  // Scanned devices (progressive, reactive)
  private readonly scannedDevicesMap = new Map<string, BleDeviceInfoExtended>();
  private readonly scannedDevicesSubject$ = new BehaviorSubject<Array<BleDeviceInfoExtended>>([]);

  // Public observables
  public readonly connectionState$ = this.connectionStateSubject$.asObservable();
  public readonly connectedDevice$ = this.connectedDeviceSubject$.asObservable();
  public readonly deviceData$ = this.deviceDataSubject$.asObservable();
  public readonly scannedDevices$ = this.scannedDevicesSubject$.asObservable();
  public readonly bleEnabled$ = this.bleEnabledSubject$.asObservable();

  private currentDeviceId: string | undefined = undefined;
  private currentAdapter: BaseDeviceAdapter | undefined = undefined;
  private readonly cancelScan$$ = new Subject<void>();

  private readonly dti6Adapter = inject(Dti6DeviceAdapter);
  private readonly cannesAdapter = inject(CannesDeviceAdapter);

  private initialized = false;

  constructor() {
    this.adapters = new Map<DeviceType, BaseDeviceAdapter>([
      [DeviceType.DTI_6, this.dti6Adapter],
      [DeviceType.CANNES, this.cannesAdapter],
    ]);
  }

  private initializeBle$(): Observable<void> {
    if (this.initialized) {
      return of(undefined);
    }

    return from(BleClient.initialize({ androidNeverForLocation: true })).pipe(
      switchMap(() => from(BleClient.startEnabledNotifications(enabled => this.onBleAdapterStateChange(enabled)))),
      tap(() => {
        this.initialized = true;
      }),
      catchError((error: Error) => {
        this.initialized = false;
        this.connectionStateSubject$.next(BleConnectionState.DISCONNECTED);
        const errorMessage = identifyBleErrorType(error);
        return throwError(() => ({
          success: false,
          error: errorMessage,
        }));
      })
    );
  }

  private onBleAdapterStateChange(enabled: boolean): void {
    this.bleEnabledSubject$.next(enabled);

    if (!enabled) {
      this.scannedDevicesMap.clear();
      this.scannedDevicesSubject$.next([]);
      this.clearConnection();
    }
  }

  /**
   * Progressive BLE scan that emits devices to scannedDevices$ as they're found.
   * Completes after the scan timeout (2 min). Teardown stops the scan on unsubscribe.
   */
  public scanForDevices$(): Observable<void> {
    if (!Capacitor.isNativePlatform()) {
      return throwError(() => ({
        success: false,
        error: 'Bluetooth scanning is only available on native devices (iOS/Android)',
      }));
    }

    if (!this.bleEnabledSubject$.value) {
      return throwError(() => ({
        success: false,
        error: 'Bluetooth is turned off',
      }));
    }

    this.cancelScan();
    this.connectionStateSubject$.next(BleConnectionState.SCANNING);
    this.scannedDevicesMap.clear();
    this.scannedDevicesSubject$.next([]);

    const scan$ = from(BleClient.stopLEScan().catch(() => {})).pipe(
      switchMap(() => this.initializeBle$()),
      switchMap(() => from(BleClient.isEnabled())),
      switchMap(enabled => {
        if (!enabled) {
          this.bleEnabledSubject$.next(false);
          this.connectionStateSubject$.next(BleConnectionState.DISCONNECTED);
          return throwError(() => ({
            success: false,
            error: 'Bluetooth is turned off',
          }));
        }
        return from(BleClient.isLocationEnabled()).pipe(
          catchError(() => of(true)),
          switchMap(locationEnabled => {
            if (!locationEnabled) {
              this.connectionStateSubject$.next(BleConnectionState.DISCONNECTED);
              return throwError(() => ({
                success: false,
                error: 'Location services are required for Bluetooth scanning on this device',
              }));
            }
            return this.startScanObservable$();
          })
        );
      })
    );

    return scan$.pipe(takeUntil(this.cancelScan$$));
  }

  private startScanObservable$(): Observable<void> {
    return new Observable<void>(observer => {
      const scanTimeout = setTimeout(() => {
        BleClient.stopLEScan()
          .then(() => {
            // Only update connection state if we're still scanning – a connect may have
            // already moved the state to CONNECTING/CONNECTED while the timer was pending.
            if (this.connectionStateSubject$.value === BleConnectionState.SCANNING) {
              this.connectionStateSubject$.next(this.scannedDevicesMap.size > 0 ? BleConnectionState.DEVICE_FOUND : BleConnectionState.DISCONNECTED);
            }
            observer.next();
            observer.complete();
          })
          .catch((error: Error) => {
            clearTimeout(scanTimeout);
            if (this.connectionStateSubject$.value === BleConnectionState.SCANNING) {
              this.connectionStateSubject$.next(BleConnectionState.DISCONNECTED);
            }
            const errorMessage = identifyBleErrorType(error);
            observer.error({
              success: false,
              error: errorMessage,
            });
          });
      }, this.SCAN_TIMEOUT_MS);

      BleClient.requestLEScan(
        {
          allowDuplicates: false,
          scanMode: ScanMode.SCAN_MODE_LOW_LATENCY,
        },
        result => {
          const deviceName = result.localName ?? result.device.name ?? '';
          const deviceType = this.identifyDeviceType(deviceName, result.uuids);

          if (deviceType !== DeviceType.UNKNOWN && !this.scannedDevicesMap.has(result.device.deviceId)) {
            const deviceInfo: BleDeviceInfoExtended = {
              deviceId: result.device.deviceId,
              name: deviceName,
              isConnected: false,
              deviceType,
            };

            this.scannedDevicesMap.set(result.device.deviceId, deviceInfo);
            this.scannedDevicesSubject$.next(Array.from(this.scannedDevicesMap.values()));
          }
        }
      ).catch((error: Error) => {
        clearTimeout(scanTimeout);
        if (this.connectionStateSubject$.value === BleConnectionState.SCANNING) {
          this.connectionStateSubject$.next(BleConnectionState.DISCONNECTED);
        }
        const errorMessage = identifyBleErrorType(error);
        observer.error({
          success: false,
          error: errorMessage,
        });
      });

      return () => {
        clearTimeout(scanTimeout);
        void BleClient.stopLEScan();
      };
    });
  }

  public async stopScan(): Promise<void> {
    this.cancelScan();
    await BleClient.stopLEScan().catch(() => {});
    if (this.connectionStateSubject$.value === BleConnectionState.SCANNING) {
      this.scannedDevicesMap.clear();
      this.scannedDevicesSubject$.next([]);
      this.connectionStateSubject$.next(BleConnectionState.DISCONNECTED);
    }
  }

  private cancelScan(): void {
    this.cancelScan$$.next();
  }

  private identifyDeviceType(deviceName: string, advertisedServices?: Array<string>): DeviceType {
    for (const [type, adapter] of this.adapters.entries()) {
      if (adapter.canHandleDevice(deviceName, advertisedServices)) {
        return type;
      }
    }
    return DeviceType.UNKNOWN;
  }

  public connectToDevice$(deviceId: string): Observable<BleServiceResponse<BleDeviceInfoExtended>> {
    if (!this.bleEnabledSubject$.value) {
      return throwError(() => ({
        success: false,
        error: 'Bluetooth is turned off',
      }));
    }

    const deviceInfo = this.scannedDevicesMap.get(deviceId);
    if (!deviceInfo) {
      return throwError(() => ({
        success: false,
        error: 'Device not found in scanned devices',
      }));
    }

    const adapter = this.adapters.get(deviceInfo.deviceType);
    if (!adapter) {
      return throwError(() => ({
        success: false,
        error: `No adapter found for device type: ${deviceInfo.deviceType}`,
      }));
    }

    this.cancelScan();
    return from(BleClient.stopLEScan().catch(() => {})).pipe(switchMap(() => this.connectWithAdapter$(deviceId, adapter, deviceInfo)));
  }

  private connectWithAdapter$(
    deviceId: string,
    adapter: BaseDeviceAdapter,
    deviceInfo: BleDeviceInfoExtended
  ): Observable<BleServiceResponse<BleDeviceInfoExtended>> {
    this.connectionStateSubject$.next(BleConnectionState.CONNECTING);

    const cleanup$ = this.currentDeviceId ? from(this.forceCleanup()) : of(undefined);

    return cleanup$.pipe(
      switchMap(() => adapter.connect$(deviceId, () => this.clearConnection())),
      map((result): BleServiceResponse<BleDeviceInfoExtended> => {
        if (result.success) {
          this.currentDeviceId = deviceId;
          this.currentAdapter = adapter;
          this.connectionStateSubject$.next(BleConnectionState.CONNECTED);

          const updatedDeviceInfo: BleDeviceInfoExtended = { ...deviceInfo, isConnected: true };
          this.scannedDevicesMap.set(deviceId, updatedDeviceInfo);
          this.connectedDeviceSubject$.next(updatedDeviceInfo);

          return { success: true, data: updatedDeviceInfo };
        }
        return { success: false, error: result.error ?? 'Connection failed' };
      }),
      catchError((error: unknown) => {
        this.connectionStateSubject$.next(BleConnectionState.ERROR);
        return throwError(() => error);
      })
    );
  }

  public disconnect$(): Observable<BleServiceResponse<void>> {
    if (!this.currentDeviceId || !this.currentAdapter) {
      return throwError(() => ({
        success: false,
        error: 'No device connected',
      }));
    }

    this.connectionStateSubject$.next(BleConnectionState.DISCONNECTING);

    return this.currentAdapter.disconnect$(this.currentDeviceId).pipe(
      tap(() => this.clearConnection()),
      catchError((error: unknown) => {
        this.clearConnection();
        return throwError(() => error);
      })
    );
  }

  public readBatteryLevel$(): Observable<BleServiceResponse<number>> {
    if (!this.currentDeviceId || !this.currentAdapter) {
      return throwError(() => ({
        success: false,
        error: 'No device connected',
      }));
    }

    return this.currentAdapter.readBatteryLevel$(this.currentDeviceId).pipe(
      tap(result => {
        if (result.success) {
          const currentData = this.deviceDataSubject$.value;
          this.deviceDataSubject$.next({ ...currentData, batteryLevel: result.data });
        }
      })
    );
  }

  public readBrightnessLevel$(): Observable<BleServiceResponse<number>> {
    if (!this.currentDeviceId || !this.currentAdapter) {
      return throwError(() => ({
        success: false,
        error: 'No device connected',
      }));
    }

    return this.currentAdapter.readBrightnessLevel$(this.currentDeviceId).pipe(
      tap(result => {
        if (result.success) {
          const currentData = this.deviceDataSubject$.value;
          this.deviceDataSubject$.next({ ...currentData, brightnessLevel: result.data });
        }
      })
    );
  }

  public writeBrightnessLevel$(level: number): Observable<BleServiceResponse<void>> {
    if (!this.currentDeviceId || !this.currentAdapter) {
      return throwError(() => ({
        success: false,
        error: 'No device connected',
      }));
    }

    return this.currentAdapter.writeBrightnessLevel$(this.currentDeviceId, level).pipe(
      tap(result => {
        if (result.success) {
          const currentData = this.deviceDataSubject$.value;
          this.deviceDataSubject$.next({ ...currentData, brightnessLevel: level });
        }
      })
    );
  }

  public writeContrastLevel$(level: number): Observable<BleServiceResponse<void>> {
    if (!this.currentDeviceId || !this.currentAdapter || !(this.currentAdapter instanceof CannesDeviceAdapter)) {
      return throwError(() => ({
        success: false,
        error: 'No device connected or unsupported device type',
      }));
    }

    return this.currentAdapter.writeContrastLevel$(this.currentDeviceId, level).pipe(
      tap(result => {
        if (result.success) {
          const current = this.deviceDataSubject$.value;
          this.deviceDataSubject$.next({ ...current, contrastLevel: level });
        }
      })
    );
  }

  public writeImageMode$(mode: number): Observable<BleServiceResponse<void>> {
    if (!this.currentDeviceId || !this.currentAdapter || !(this.currentAdapter instanceof CannesDeviceAdapter)) {
      return throwError(() => ({
        success: false,
        error: 'No device connected or unsupported device type',
      }));
    }

    return this.currentAdapter.writeImageMode$(this.currentDeviceId, mode).pipe(
      tap(result => {
        if (result.success) {
          const current = this.deviceDataSubject$.value;
          this.deviceDataSubject$.next({ ...current, imageMode: mode });
        }
      })
    );
  }

  public writeConnectivityEnabled$(enabled: boolean): Observable<BleServiceResponse<void>> {
    if (!this.currentDeviceId || !this.currentAdapter || !(this.currentAdapter instanceof CannesDeviceAdapter)) {
      return throwError(() => ({
        success: false,
        error: 'No device connected or unsupported device type',
      }));
    }

    return this.currentAdapter.writeConnectivityEnabled$(this.currentDeviceId, enabled);
  }

  public writeWifiEnabled$(enabled: boolean): Observable<BleServiceResponse<void>> {
    if (!this.currentDeviceId || !this.currentAdapter || !(this.currentAdapter instanceof CannesDeviceAdapter)) {
      return throwError(() => ({
        success: false,
        error: 'No device connected or unsupported device type',
      }));
    }

    return this.currentAdapter.writeWifiEnabled$(this.currentDeviceId, enabled).pipe(
      tap(result => {
        if (result.success) {
          const current = this.deviceDataSubject$.value;
          this.deviceDataSubject$.next({ ...current, wifiEnabled: enabled });
        }
      })
    );
  }

  public readAccessPointSSID$(): Observable<BleServiceResponse<string>> {
    if (!this.currentDeviceId || !this.currentAdapter || !(this.currentAdapter instanceof CannesDeviceAdapter)) {
      return throwError(() => ({
        success: false,
        error: 'No device connected or unsupported device type',
      }));
    }
    return this.currentAdapter.readAccessPointSSID$(this.currentDeviceId);
  }

  public writeStreamStart$(): Observable<BleServiceResponse<void>> {
    if (!this.currentDeviceId || !this.currentAdapter || !(this.currentAdapter instanceof CannesDeviceAdapter)) {
      return throwError(() => ({
        success: false,
        error: 'No device connected or unsupported device type',
      }));
    }
    return this.currentAdapter.writeStreamStart$(this.currentDeviceId);
  }

  public readStreamingStatus$(): Observable<BleServiceResponse<boolean>> {
    if (!this.currentDeviceId || !this.currentAdapter || !(this.currentAdapter instanceof CannesDeviceAdapter)) {
      return throwError(() => ({
        success: false,
        error: 'No device connected or unsupported device type',
      }));
    }
    return this.currentAdapter.readStreamingStatus$(this.currentDeviceId);
  }

  public writeAccessPointPassphrase$(passphrase: string): Observable<BleServiceResponse<void>> {
    if (!this.currentDeviceId || !this.currentAdapter || !(this.currentAdapter instanceof CannesDeviceAdapter)) {
      return throwError(() => ({
        success: false,
        error: 'No device connected or unsupported device type',
      }));
    }
    return this.currentAdapter.writeAccessPointPassphrase$(this.currentDeviceId, passphrase);
  }

  public writeWifiMode$(mode: number): Observable<BleServiceResponse<void>> {
    if (!this.currentDeviceId || !this.currentAdapter || !(this.currentAdapter instanceof CannesDeviceAdapter)) {
      return throwError(() => ({
        success: false,
        error: 'No device connected or unsupported device type',
      }));
    }
    return this.currentAdapter.writeWifiMode$(this.currentDeviceId, mode);
  }

  public loadSettingsData$(): Observable<{ brightnessLevel: number; wifiEnabled: boolean; contrastLevel: number; imageMode: number }> {
    if (!this.currentDeviceId || !(this.currentAdapter instanceof CannesDeviceAdapter)) {
      return throwError(() => new Error('No device connected or unsupported device type'));
    }

    return this.currentAdapter.loadSettingsData$(this.currentDeviceId);
  }

  public readDeviceInfo$(): Observable<BleServiceResponse<Partial<DeviceData>>> {
    if (!this.currentDeviceId || !this.currentAdapter) {
      return throwError(() => ({
        success: false,
        error: 'No device connected',
      }));
    }

    return this.currentAdapter.readDeviceInfo$(this.currentDeviceId).pipe(
      tap(result => {
        if (result.success) {
          const currentData = this.deviceDataSubject$.value;
          this.deviceDataSubject$.next({ ...currentData, ...result.data });
        }
      })
    );
  }

  public loadInitialData$(): Observable<void> {
    if (!this.currentDeviceId || !this.currentAdapter) {
      return throwError(() => new Error('No device connected'));
    }
    this.deviceDataSubject$.next({});
    return this.currentAdapter.loadInitialData$(this.currentDeviceId, (data: Partial<DeviceData>) => {
      const current = this.deviceDataSubject$.value;
      this.deviceDataSubject$.next({ ...current, ...data });
    });
  }

  public readBleLocalName$(): Observable<BleServiceResponse<string>> {
    if (!this.currentDeviceId || !this.currentAdapter) {
      return throwError(() => ({
        success: false,
        error: 'No device connected',
      }));
    }

    if ('readBleLocalName' in this.currentAdapter) {
      return (this.currentAdapter as CannesDeviceAdapter).readBleLocalName(this.currentDeviceId).pipe(
        tap(result => {
          if (result.success && result.data) {
            const current = this.deviceDataSubject$.value;
            this.deviceDataSubject$.next({ ...current, bleLocalName: result.data });
          }
        })
      );
    }

    return throwError(() => ({
      success: false,
      error: 'readBleLocalName not supported on this device',
    }));
  }

  public writeBleLocalName$(name: string): Observable<BleServiceResponse<void>> {
    if (!this.currentDeviceId || !this.currentAdapter) {
      return throwError(() => ({
        success: false,
        error: 'No device connected',
      }));
    }

    if ('writeBleLocalName' in this.currentAdapter) {
      return (this.currentAdapter as CannesDeviceAdapter).writeBleLocalName(this.currentDeviceId, name);
    }

    return throwError(() => ({
      success: false,
      error: 'writeBleLocalName not supported on this device',
    }));
  }

  private async forceCleanup(): Promise<void> {
    if (!this.currentDeviceId) {
      return;
    }

    try {
      await BleClient.disconnect(this.currentDeviceId);
    } catch {
      // Expected when no active connection exists
    }

    this.clearConnection();
    await new Promise(resolve => setTimeout(resolve, this.CLEANUP_DELAY_MS));
  }

  private clearConnection(): void {
    this.currentDeviceId = undefined;
    this.currentAdapter = undefined;
    this.connectionStateSubject$.next(BleConnectionState.DISCONNECTED);
    this.connectedDeviceSubject$.next(undefined);
    this.deviceDataSubject$.next({});
  }

  public get isConnected(): boolean {
    return this.connectionStateSubject$.value === BleConnectionState.CONNECTED;
  }

  public get currentConnectionState(): BleConnectionState {
    return this.connectionStateSubject$.value;
  }
}
